import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../../firebase-client';
import { getCurrencyMeta } from '../../../../utils/currency';

const dateLabel = (value) => {
  const date = value ? new Date(value) : null;
  return date && Number.isFinite(date.getTime()) ? date.toLocaleDateString('en-GB') : 'Not available';
};

export default function BoltOnMembershipYearReanchorModal({ isOpen, teamId, currency = 'GBP', onClose, onApplied, embedded = false, draft = null, onBusyChange, resumingChange = false, initialFinancialMode = 'next_renewal' }) {
  const titleRef = useRef(null);
  useEffect(() => { if (isOpen) titleRef.current?.focus(); }, [isOpen]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { onBusyChange?.(applying); }, [applying, onBusyChange]);
  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);
  const [financialMode, setFinancialMode] = useState(initialFinancialMode);
  const { symbol, decimals } = getCurrencyMeta(preview?.currency || currency);
  const money = (minor) => `${symbol}${(Number(minor || 0) / (10 ** decimals)).toFixed(decimals)}`;

  const loadPreview = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setError('');
    try {
      const result = await httpsCallable(functions, 'previewBoltOnMembershipYearReanchor')({ teamId, ...(draft ? { draft } : {}) });
      setPreview(result?.data || null);
    } catch (err) {
      setPreview(null);
      setError(err?.message || 'Unable to preview bolt-on alignment.');
    } finally {
      setLoading(false);
    }
  }, [teamId, draft]);

  useEffect(() => {
    if (isOpen) { setFinancialMode(initialFinancialMode); loadPreview(); }
    else {
      setPreview(null);
      setError('');
      setFinancialMode('next_renewal');
    }
  }, [isOpen, loadPreview, initialFinancialMode]);

  const counts = useMemo(() => preview?.counts || {}, [preview]);
  const apply = async () => {
    if (!preview?.runId) return;
    setApplying(true);
    setError('');
    try {
      const result = await httpsCallable(functions, 'applyBoltOnMembershipYearReanchor')({
        teamId,
        runId: preview.runId,
        financialMode,
        ...(draft ? { draft, baseRevision: preview.baseRevision } : {}),
      });
      if (Number(result?.data?.counts?.applyFailed || 0) > 0 || (draft && result?.data?.settingsSaved === false)) {
        setError(`${Number(result.data.counts.applied || 0)} purchases updated; ${Number(result.data.counts.applyFailed || result.data.counts.failed || 0)} could not be updated. Review these purchases before retrying.`);
        return;
      }
      await onApplied?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Unable to apply bolt-on alignment.');
    } finally {
      setApplying(false);
    }
  };

  if (!isOpen) return null;
  const content = <>
      <h2 ref={titleRef} tabIndex={-1} className="modal-title">Align existing bolt-ons</h2>
      {resumingChange && <p className="form-hint">An earlier confirmed change has not finished. The saved club renewal settings are still active. This preview checks the remaining purchases; changes already applied will not be repeated. If it is still processing, refresh shortly.</p>}
      {error && <div className="form-error">{error}</div>}
      {loading ? <p className="text-muted">Preparing preview…</p> : preview && <div className="modal-form">
        <p className="text-muted">{draft ? (resumingChange ? 'Review and finish the previously confirmed renewal change.' : 'Review the renewal change before saving. New-purchase settings and existing-purchase handling are confirmed together. Nothing has been saved by opening this review.') : 'Choose how existing purchases move to the club’s annual renewal date. New purchases already use your saved renewal settings. Existing purchases change only when you apply this preview.'}</p>
        <div className="bolt-on-settings-grid bolt-on-settings-grid--summary">
          {[
            ['Eligible', counts.totalEligible], ['Manual', counts.manual], ['Stripe', counts.subscription],
            ...(['dates_only', 'adjust'].includes(financialMode) ? [['Earlier', counts.shortened], ['Later', counts.extended]] : []), ['Already aligned', counts.alreadyAligned],
            ['Manual review', counts.manualReview], ['Skipped', counts.skipped], ['Failed', counts.failed],
          ].map(([label, value]) => <div className="bolt-on-settings-card" key={label}><strong>{Number(value || 0)}</strong><span>{label}</span></div>)}
        </div>
        {financialMode === 'adjust' && <div className="form-hint">
          <div><strong>Potential credits:</strong> {money(preview?.totals?.creditMinor)}</div>
          <div><strong>Potential amounts due:</strong> {money(preview?.totals?.amountDueMinor)}</div>
        </div>}
        <div className="form-group">
          <label htmlFor="bolt-on-transition-method">How should existing purchases change?</label>
          <select id="bolt-on-transition-method" className="input" disabled={applying || resumingChange} value={financialMode} onChange={(event) => setFinancialMode(event.target.value)}>
            <option value="next_renewal">Finish paid period, then pro-rata (recommended)</option>
            <option value="dates_only">Move renewal date now without a price adjustment</option>
            <option value="adjust">Move renewal date now with credits or additional charges</option>
            {draft && <option value="keep_existing">Keep existing renewal patterns; save for new purchases only</option>}
          </select>
        </div>
        <p className="form-hint">{financialMode === 'keep_existing' ? 'Save the new renewal settings for future purchases. Existing purchases keep their current renewal pattern; no existing dates, charges or credits are changed.' : financialMode === 'next_renewal'
          ? 'Keep each member’s current paid-through date and next payment date. At the end of that period, charge only for the shorter period up to the next club renewal date, then renew annually. No charge or credit is made today. The joining full-price window does not apply to this pro-rata period.'
          : financialMode === 'adjust'
            ? 'Change renewal dates now. Shortening a paid period creates a credit against a future payment; extending it creates an additional amount due now (charged automatically for subscriptions).'
            : 'Change renewal dates now without a credit or additional charge. Members may lose time they already paid for, or receive extra time free.'}</p>
        {draft && Number(counts.totalEligible || 0) === 0 && <p className="form-hint">No eligible existing purchases need a renewal change. You can confirm the settings for future purchases.</p>}
        {Number(counts.manualReview || 0) > 0 && <p className="form-hint">{counts.manualReview} purchases need manual review and will not be changed, including unpaid purchases, discounted subscriptions and purchases already awaiting a change.</p>}
        {Array.isArray(preview.samples) && preview.samples.length > 0 && <div className="bolt-on-reanchor-samples">
          {preview.samples.map((sample, index) => <div className="bolt-on-settings-card" key={`${sample.memberName}-${sample.boltOnName}-${index}`}>
            <strong>{sample.memberName}</strong>
            <span>{sample.boltOnName}</span>
            {financialMode === 'keep_existing' ? <small>Current renewal pattern stays unchanged.</small> : financialMode === 'next_renewal' ? <>
              <small>Paid through: {dateLabel(sample.paidThrough)} (unchanged)</small>
              <small>Pro-rata payment on {dateLabel(sample.bridgeStart)}: {money(sample.bridgeChargeAmount)} · covers until {dateLabel(sample.alignedRenewalDate)}</small>
              <small>Annual renewal from {dateLabel(sample.alignedRenewalDate)}: {money(sample.fullChargeAmount)} per year</small>
            </> : <small className="text-muted">Renewal: {dateLabel(sample.previousDate)} → {dateLabel(sample.targetDate)} · {financialMode === 'dates_only' ? 'no price adjustment' : sample.direction === 'shortened' ? `future credit ${money(sample.adjustmentMinor)}` : sample.direction === 'extended' ? `due now ${money(sample.adjustmentMinor)}` : 'no change'}</small>}
          </div>)}
        </div>}
        {preview.hasMoreSamples && <p className="form-hint">Showing the first {preview.samples.length} of {counts.totalEligible} eligible purchases. Your choice applies to all eligible purchases.</p>}
        <div className="form-actions">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={applying}>{resumingChange ? 'Close unfinished review' : draft ? 'Back without saving' : 'Keep existing dates'}</button>
          <button type="button" className="btn btn--secondary" onClick={loadPreview} disabled={loading || applying}>Refresh</button>
          <button type="button" className="btn btn--primary" onClick={apply} disabled={loading || applying || (!draft && Number(counts.totalEligible || 0) === 0)}>{applying ? 'Saving and applying…' : draft ? (financialMode === 'keep_existing' ? 'Save for new purchases only' : 'Confirm save and apply') : financialMode === 'next_renewal' ? 'Schedule future alignment' : 'Apply immediate alignment'}</button>
        </div>
      </div>}
      {(!preview || loading) && <div className="form-actions"><button type="button" className="btn btn--ghost" onClick={onClose} disabled={applying}>{resumingChange ? 'Close unfinished review' : draft ? 'Back without saving' : 'Keep existing dates'}</button>{error && <button type="button" className="btn btn--secondary" onClick={loadPreview}>Retry preview</button>}</div>}
  </>;
  if (embedded) return <div className="bolt-on-workspace">{content}</div>;
  return <div className="modal-backdrop" onClick={applying ? undefined : onClose}>
    <div className="modal-content modal-content--bolt-on-settings" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>{content}</div>
  </div>;
}
